/**
 * ReservationsItemsTab - Main tab for item reservations (golf carts, equipment, etc.)
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useReservations } from '../../reservations.shared/context/ReservationsContext';
import useItemReservations from '../hooks/useItemReservations';
import ItemReservationCard from './ItemReservationCard';
import ItemFilters from './ItemFilters';
import ItemReservationModal from './ItemReservationModal';
import ItemReturnModal from './ItemReturnModal';
import ReservationDetailsModal from '../../reservations.shared/components/ReservationDetailsModal';
import LoadingSpinner from '../../../common/LoadingSpinner';
import EmptyState from '../../../common/EmptyState';
import Pagination from '../../../common/Pagination';
import { PlusIcon, AdjustmentsHorizontalIcon, TruckIcon } from '@heroicons/react/24/outline';
import { calculateRentalStatus, RENTAL_STATUS } from '../utils/rentalStatusUtils';
import axios from 'axios';

const ReservationsItemsTab = ({ globalFilters = {} }) => {
  const { canProcessReturns, hasPermission, isAdmin, token } = useAuth();
  const { orgTaxRate, fetchTaxRate, inventoryItems, fetchInventoryItems } = useReservations();

  // Rental notification settings
  const [rentalSettings, setRentalSettings] = useState({
    maxRentalDurationHours: 0,
    notificationFlagMinutes: 0
  });
  const [currentTime, setCurrentTime] = useState(new Date());

  // Debug: Log tax rate when it changes
  useEffect(() => {
    console.log('[ReservationsItemsTab] orgTaxRate changed:', orgTaxRate);
  }, [orgTaxRate]);
  
  const {
    reservations,
    loading,
    error,
    filters,
    pagination,
    sortConfig,
    fetchReservations,
    processReturn,
    cancelReservation,
    reprocessReservation,
    deleteReservation,
    applyFilters,
    sortReservations,
    setPagination
  } = useItemReservations();

  // Modal states
  const [showNewReservationModal, setShowNewReservationModal] = useState(false);
  const [showEditReservationModal, setShowEditReservationModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [detailedReservation, setDetailedReservation] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Initialize data on mount
  useEffect(() => {
    Promise.all([
      fetchTaxRate(),
      fetchInventoryItems()
    ]).catch(console.error);
  }, [fetchTaxRate, fetchInventoryItems]);

  // Fetch rental notification settings
  useEffect(() => {
    const fetchRentalSettings = async () => {
      try {
        const response = await axios.get('/api/settings/organization/general', {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data) {
          setRentalSettings({
            maxRentalDurationHours: response.data.maxRentalDurationHours || 0,
            notificationFlagMinutes: response.data.notificationFlagMinutes || 0
          });
        }
      } catch (error) {
        console.error('Error fetching rental notification settings:', error);
      }
    };

    fetchRentalSettings();
  }, [token]);

  // Update current time every minute to refresh rental status indicators
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Apply global filters when they change
  useEffect(() => {
    if (globalFilters && Object.keys(globalFilters).length > 0) {
      const mergedFilters = {
        ...filters,
        ...globalFilters
      };
      applyFilters(mergedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilters]);

  // Handle actions
  const handleViewDetails = async (reservation) => {
    try {
      // Fetch complete reservation details from the API
      const response = await axios.get(`/api/reservations/items/${reservation.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data?.success && response.data?.data) {
        setDetailedReservation(response.data.data);
        setSelectedReservation(reservation);
        setShowDetailModal(true);
      } else {
        console.error('Failed to fetch reservation details:', response.data);
        // Fallback to list data if API fails
        setDetailedReservation(reservation);
        setSelectedReservation(reservation);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Error fetching reservation details:', error);
      // Fallback to list data if API fails
      setDetailedReservation(reservation);
      setSelectedReservation(reservation);
      setShowDetailModal(true);
    }
  };

  const handleProcessReturn = async (reservation) => {
    try {
      // Fetch complete reservation details from the API
      const response = await axios.get(`/api/reservations/items/${reservation.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data?.success && response.data?.data) {
        setSelectedReservation(response.data.data);
        setShowReturnModal(true);
      } else {
        console.error('Failed to fetch reservation details:', response.data);
        // Fallback to list data if API fails
        setSelectedReservation(reservation);
        setShowReturnModal(true);
      }
    } catch (error) {
      console.error('Error fetching reservation details:', error);
      // Fallback to list data if API fails
      setSelectedReservation(reservation);
      setShowReturnModal(true);
    }
  };

  const handleEdit = async (reservation) => {
    try {
      // Fetch complete reservation details from the API
      const response = await axios.get(`/api/reservations/items/${reservation.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data?.success && response.data?.data) {
        setSelectedReservation(response.data.data);
        setShowEditReservationModal(true);
      } else {
        console.error('Failed to fetch reservation details:', response.data);
        // Fallback to list data if API fails
        setSelectedReservation(reservation);
        setShowEditReservationModal(true);
      }
    } catch (error) {
      console.error('Error fetching reservation for edit:', error);
      // Fallback to list data if API fails
      setSelectedReservation(reservation);
      setShowEditReservationModal(true);
    }
  };

  const handleReturnSubmit = async (returnData) => {
    try {
      await processReturn(selectedReservation.id, returnData);
      setShowReturnModal(false);
      setSelectedReservation(null);
      await fetchReservations(); // Refresh data
      await fetchInventoryItems(); // Refresh available items
    } catch (error) {
      console.error('Failed to process return:', error);
      // Error is already handled by the hook
    }
  };

  const handleCancelReservation = async (reservationId, cancelData) => {
    try {
      await cancelReservation(reservationId, cancelData);
      await fetchReservations(); // Refresh data
      await fetchInventoryItems(); // Refresh available items
    } catch (error) {
      console.error('Failed to cancel reservation:', error);
    }
  };

  const handleReprocessReservation = async (reservationId) => {
    try {
      await reprocessReservation(reservationId, { status: 'ACTIVE' });
      await fetchReservations(); // Refresh data
      await fetchInventoryItems(); // Refresh available items
    } catch (error) {
      console.error('Failed to reprocess reservation:', error);
    }
  };

  const handleDeleteReservation = async (reservationId) => {
    if (window.confirm('Are you sure you want to delete this reservation? This action cannot be undone.')) {
      try {
        await deleteReservation(reservationId);
        await fetchReservations(); // Refresh data
        await fetchInventoryItems(); // Refresh available items
      } catch (error) {
        console.error('Failed to delete reservation:', error);
      }
    }
  };

  const handleNewReservationSuccess = async () => {
    setShowNewReservationModal(false);
    await fetchReservations(); // Refresh data
    await fetchInventoryItems(); // Refresh available items
  };

  const handleEditReservationSuccess = async () => {
    setShowEditReservationModal(false);
    setSelectedReservation(null);
    await fetchReservations(); // Refresh data
    await fetchInventoryItems(); // Refresh available items
  };

  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, currentPage: newPage });
  };

  const handleSort = (key) => {
    sortReservations(key);
  };

  // Apply client-side rental status filtering
  const filteredReservations = reservations.filter((reservation) => {
    // If no rental status filter is applied, show all
    if (!filters.rentalStatus || filters.rentalStatus === 'all') {
      return true;
    }

    // Calculate rental status for this reservation
    const rentalStatus = calculateRentalStatus(
      reservation,
      rentalSettings.maxRentalDurationHours,
      rentalSettings.notificationFlagMinutes
    );

    // Filter based on selected rental status
    return rentalStatus === filters.rentalStatus;
  });

  if (loading && reservations.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header - Mobile Optimized */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Item Reservations</h2>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">Golf carts, equipment, and other rental items</p>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-3 py-2.5 sm:py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 touch-manipulation"
          >
            <AdjustmentsHorizontalIcon className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Filters</span>
          </button>
          {hasPermission('reservations', 'create') && (
            <button
              onClick={() => setShowNewReservationModal(true)}
              className="inline-flex items-center px-4 py-2.5 sm:py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 touch-manipulation"
            >
              <PlusIcon className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">New</span>
              <span className="hidden sm:inline">Reservation</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <ItemFilters
            filters={filters}
            onFiltersChange={applyFilters}
            onClose={() => setShowFilters(false)}
          />
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => fetchReservations()}
                  className="text-sm bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay for Updates */}
      {loading && reservations.length > 0 && (
        <div className="flex justify-center py-4">
          <div className="flex items-center text-sm text-gray-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            Updating reservations...
          </div>
        </div>
      )}

      {/* Reservations List */}
      {filteredReservations.length === 0 && !loading ? (
        <EmptyState
          icon={TruckIcon}
          title="No Item Reservations Found"
          description="No reservations match your current filters. Try adjusting your search criteria or create a new reservation."
          action={hasPermission('reservations', 'create') ? {
            label: "Create New Reservation",
            onClick: () => setShowNewReservationModal(true)
          } : undefined}
        />
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                    onClick={() => handleSort('customer.lastName')}
                  >
                    <div className="flex items-center">
                      Customer
                      {sortConfig.key === 'customer.lastName' && (
                        <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                    onClick={() => handleSort('inventoryItem.itemNumber')}
                  >
                    <div className="flex items-center">
                      Item
                      {sortConfig.key === 'inventoryItem.itemNumber' && (
                        <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                    onClick={() => handleSort('startTime')}
                  >
                    <div className="flex items-center">
                      Start Time
                      {sortConfig.key === 'startTime' && (
                        <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                    onClick={() => handleSort('endTime')}
                  >
                    <div className="flex items-center">
                      End Time
                      {sortConfig.key === 'endTime' && (
                        <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Deposit
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status
                      {sortConfig.key === 'status' && (
                        <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReservations.map((reservation) => {
                  const formatDate = (dateString) => {
                    if (!dateString) return '—';
                    // Ensure we're parsing as UTC and converting to local time
                    const date = new Date(dateString + (dateString.includes('Z') ? '' : 'Z'));
                    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  };

                  const formatTime = (dateString) => {
                    if (!dateString) return '—';
                    // Ensure we're parsing as UTC and converting to local time
                    const date = new Date(dateString + (dateString.includes('Z') ? '' : 'Z'));
                    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                  };

                  const getStatusColor = (status) => {
                    switch (status) {
                      case 'ACTIVE': return 'text-green-600 bg-green-50';
                      case 'COMPLETED': return 'text-blue-600 bg-blue-50';
                      case 'CANCELLED': return 'text-red-600 bg-red-50';
                      default: return 'text-gray-600 bg-gray-50';
                    }
                  };

                  // Calculate rental status for visual indicator
                  const rentalStatus = calculateRentalStatus(
                    reservation,
                    rentalSettings.maxRentalDurationHours,
                    rentalSettings.notificationFlagMinutes
                  );

                  // Determine row styling based on rental status
                  let rowClass = 'hover:bg-gray-50';
                  if (rentalStatus === RENTAL_STATUS.OVERDUE) {
                    rowClass = 'bg-red-50 border-l-4 border-red-500 hover:bg-red-100';
                  } else if (rentalStatus === RENTAL_STATUS.WARNING) {
                    rowClass = 'bg-yellow-50 border-l-4 border-yellow-400 hover:bg-yellow-100';
                  }

                  return (
                    <tr key={reservation.id} className={rowClass}>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {reservation.customer?.firstName} {reservation.customer?.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {reservation.customer?.phone}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {reservation.inventoryItem?.itemNumber || '—'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {reservation.inventoryItem?.productType?.name || 'Item'}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(reservation.startTime)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatTime(reservation.startTime)}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                        {reservation.reservationDuration || 0}h
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(reservation.endTime)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatTime(reservation.endTime)}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                        {reservation.depositAmount ? `$${reservation.depositAmount.toFixed(2)}` : '—'}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                        {reservation.totalAmount ? `$${reservation.totalAmount.toFixed(2)}` : '—'}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(reservation.status)}`}>
                          {reservation.status}
                        </span>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                        {reservation.createdByEmployee?.firstName} {reservation.createdByEmployee?.lastName || ''}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-left text-sm font-medium">
                        <div className="flex items-center justify-start space-x-2">
                          {hasPermission('reservations', 'view') && (
                            <button
                              onClick={() => handleViewDetails(reservation)}
                              className="text-blue-600 hover:text-blue-900"
                              title="View Details"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                          )}
                          {reservation.status === 'ACTIVE' && hasPermission('reservations', 'create') && (
                            <button
                              onClick={() => handleEdit(reservation)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                          {reservation.status === 'ACTIVE' && canProcessReturns && (
                            <button
                              onClick={() => handleProcessReturn(reservation)}
                              className="text-green-600 hover:text-green-900"
                              title="Process Return"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </button>
                          )}
                          {hasPermission('reservations', 'delete') && (
                            <button
                              onClick={() => handleDeleteReservation(reservation.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {filteredReservations.map((reservation) => (
              <ItemReservationCard
                key={reservation.id}
                reservation={reservation}
                onViewDetails={handleViewDetails}
                onProcessReturn={handleProcessReturn}
                onCancel={handleCancelReservation}
                onReprocess={handleReprocessReservation}
                onDelete={handleDeleteReservation}
                onEdit={handleEdit}
                canProcessReturns={canProcessReturns}
                taxRate={orgTaxRate}
              />
            ))}
          </div>
        </>
      )}

      {/* Pagination - Mobile Optimized */}
      {filteredReservations.length > 0 && pagination.totalItems > pagination.itemsPerPage && (
        <div className="flex justify-center mt-6 sm:mt-8 px-4 sm:px-0">
          <Pagination
            currentPage={pagination.currentPage}
            totalItems={pagination.totalItems}
            itemsPerPage={pagination.itemsPerPage}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Modals */}
      {showNewReservationModal && (
        <ItemReservationModal
          isOpen={showNewReservationModal}
          onClose={() => setShowNewReservationModal(false)}
          onSuccess={handleNewReservationSuccess}
          inventoryItems={inventoryItems}
          taxRate={orgTaxRate}
        />
      )}

      {showEditReservationModal && selectedReservation && (
        <ItemReservationModal
          isOpen={showEditReservationModal}
          onClose={() => {
            setShowEditReservationModal(false);
            setSelectedReservation(null);
          }}
          onSuccess={handleEditReservationSuccess}
          inventoryItems={inventoryItems}
          taxRate={orgTaxRate}
          editMode={true}
          existingReservation={selectedReservation}
        />
      )}

      {showReturnModal && selectedReservation && (
        <ItemReturnModal
          isOpen={showReturnModal}
          reservation={selectedReservation}
          onClose={() => {
            setShowReturnModal(false);
            setSelectedReservation(null);
          }}
          onSubmit={handleReturnSubmit}
          taxRate={orgTaxRate}
        />
      )}

      {showDetailModal && detailedReservation && (
        <ReservationDetailsModal
          isOpen={showDetailModal}
          reservation={detailedReservation}
          onClose={() => {
            setShowDetailModal(false);
            setDetailedReservation(null);
            setSelectedReservation(null);
          }}
          onReprocess={canProcessReturns ? handleReprocessReservation : null}
          onDelete={hasPermission('reservations', 'delete') ? handleDeleteReservation : null}
          taxRate={orgTaxRate}
          canProcessReturns={canProcessReturns}
          isAdmin={isAdmin}
          onReservationUpdate={(reservationId, updates) => {
            // Update the detailed reservation with new notes
            if (detailedReservation?.id === reservationId) {
              setDetailedReservation({
                ...detailedReservation,
                ...updates
              });
            }

            // Refresh the reservations list to show updated notes
            fetchReservations();
          }}
        />
      )}
    </div>
  );
};

export default ReservationsItemsTab;